import { useFormContext } from "react-hook-form";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  NotebookPen,
  Bell,
  ArrowLeft,
  Save,
  EllipsisVertical,
  X,
  ClipboardList,
  ArrowRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ControlledSelectV2 } from "@/components/ui/customs/controlled-select-v2";
import { Option } from "@/components/ui/customs/select-v2";
import { GetOneExpeditionBoResponseDto } from "@/types/api/api-types";
import { QuotationUpdateBoFormData } from "@/types/forms/bo/expeditionBo.form";
import { ActionDataBo } from "@/types/tableAction";
import { useNavigate } from "react-router-dom";
import { getExpeditionIdentifier } from "@/lib/expedition.util";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ExpeditionProcessStatusEnum } from "@/constants/enums/expeditionProcessStatus.enum";
import { toast } from "react-toastify";
import { NotesModal } from "@/components/shared/modal/notes.modal";
import { NotificationModal } from "@/components/shared/modal/notification.modal";
import { SaveDraftModal } from "@/components/backOffice/updateExpedition/saveDraft.modal";
import { CustomPolicyModal } from "@/components/backOffice/expeditionForm/customPolicy.modal";
import { ExpeditionTypeEnum } from "@/constants/enums/expeditionType.enum";
import { useFinalizeApiOptions } from "@/hooks/useEnumOptions";

export type ExpeditionHeaderProps = {
  expedition: GetOneExpeditionBoResponseDto;
  /** When set, renders the API mode select bound to this field (quotation finalize flow). */
  formStep?: number;
  setStep?: (step: number) => void;
  mode?: "finalize" | "updateFinalized";
};

export const ExpeditionHeader = ({
  expedition,
  formStep = 1,
  setStep,
  mode = "finalize",
}: ExpeditionHeaderProps) => {
  const { t } = useTranslation("boExpeditionForm");
  const isUpdateFinalized = mode === "updateFinalized";
  const navigate = useNavigate();
  const methods = useFormContext<QuotationUpdateBoFormData>();
  const [actionData, setActionData] = useState<ActionDataBo | null>(null);

  const expeditionIdentifier = getExpeditionIdentifier(expedition);
  const hasUnreadNotif =
    (expedition.notification?.length ?? 0) > 0 && expedition.notification?.[0]?.readBy == null;

  // The draft modal can only be opened if the expedition is pending pelican (not finalized yet)
  // And for model reasons, we need to trigger the delivery content and wood box fields
  const openDraftModal = async () => {
    if (expedition.processStatus !== ExpeditionProcessStatusEnum.PENDING_PELICAN) return;

    const trigger = await methods.trigger(["quotationWoodBox", "options"]);

    if (!trigger) {
      toast.error(t("expeditionHeader.draftRequiredFields"));
      return;
    }

    setActionData({ expedition, action: "DRAFT" });
  };

  const draftBy = expedition.draftBy
    ? `(${expedition.draftBy?.firstName?.charAt(0).toUpperCase()}${expedition.draftBy?.lastName?.charAt(0).toUpperCase()})`
    : "";

  const customId = expedition.quotation?.customId ?? expedition.customQuotation?.customId;

  return (
    <div className="sticky top-0 z-10 flex flex-col justify-between gap-4 border-b bg-transparent p-4 sm:flex-row lg:gap-0">
      <div className="flex flex-col items-start justify-between gap-1">
        <div className="flex flex-row items-center justify-between gap-4">
          <h3 className="text-lg font-semibold text-black">{expeditionIdentifier}</h3>
          {!isUpdateFinalized && draftBy && (
            <Badge variant="white" className="text-xs font-bold text-black">
              {t("expeditionHeader.draftBadge", { initials: draftBy })}
            </Badge>
          )}
        </div>
        <div className="flex flex-row items-center justify-between gap-2">
          {isUpdateFinalized ? (
            <p className="text-sm text-gray-500">{t("expeditionHeader.updateSubtitle")}</p>
          ) : (
            <>
              <p className="text-sm text-gray-500">
                {t("expeditionHeader.finalizeSubtitle", { step: formStep, total: 2 })}
              </p>
              <Button
                variant="outline"
                onClick={() => setStep?.(1)}
                type="button"
                className="h-10 w-10 [&_svg]:h-6 [&_svg]:w-6"
                disabled={formStep === 1}
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                onClick={() => setStep?.(2)}
                type="button"
                className="h-10 w-10 disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:h-6 [&_svg]:w-6"
                disabled={formStep === 2 || expedition.finalizeIndex === 1}
              >
                <ArrowRight className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>
      </div>
      <div className="flex flex-row items-center justify-between gap-2">
        <ExpeditionHeaderApiEnumSelect
          formStep={formStep}
          processStatus={expedition.processStatus as ExpeditionProcessStatusEnum}
          expeditionType={
            expedition.customQuotation
              ? ExpeditionTypeEnum.CUSTOM_QUOTATION
              : ExpeditionTypeEnum.QUOTATION
          }
        />
        {/* Desktop buttons */}
        <div className="hidden flex-row items-center justify-between gap-2 lg:flex">
          <Button
            variant="outline"
            onClick={() => setActionData({ expedition, action: "NOTES" })}
            className="relative h-12 w-12 [&_svg]:h-6 [&_svg]:w-6"
            type="button"
          >
            <NotebookPen className="h-4 w-4" />
            <Badge variant="destructive" className="absolute top-0 right-0 h-4 w-4">
              {expedition.expeditionNotes?.length}
            </Badge>
          </Button>
          <Button
            variant="outline"
            onClick={() => setActionData({ expedition, action: "NOTIFICATION" })}
            className={`relative h-12 w-12 [&_svg]:h-6 [&_svg]:w-6 ${hasUnreadNotif ? "bg-yellow-400 text-white hover:bg-yellow-500" : ""}`}
            type="button"
          >
            <Bell className="h-4 w-4" />
            <Badge variant="destructive" className="absolute top-0 right-0 h-4 w-4">
              {expedition.notification?.length}
            </Badge>
          </Button>
          <Button
            variant="outline"
            onClick={() => openDraftModal()}
            type="button"
            className={`h-12 w-12 [&_svg]:h-6 [&_svg]:w-6 ${(isUpdateFinalized || expedition.processStatus === "VALIDATED") && "hidden"}`}
          >
            <Save className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            onClick={() => setActionData({ expedition, action: "CUSTOM_POLICY" })}
            type="button"
            className={`h-12 w-12 [&_svg]:h-6 [&_svg]:w-6`}
          >
            <ClipboardList className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            type="button"
            className={`h-12 w-12 [&_svg]:h-6 [&_svg]:w-6`}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        {/* Mobile buttons */}
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="h-12 w-12 lg:hidden [&_svg]:h-6 [&_svg]:w-6">
              <EllipsisVertical className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-fit">
            <Button
              variant="outline"
              onClick={() => setActionData({ expedition, action: "NOTES" })}
              className="relative mb-2 flex h-12 w-12 [&_svg]:h-6 [&_svg]:w-6"
              type="button"
            >
              <NotebookPen className="h-4 w-4" />
              <Badge variant="destructive" className="absolute top-0 right-0 h-4 w-4">
                {expedition.expeditionNotes?.length}
              </Badge>
            </Button>
            <Button
              variant="outline"
              onClick={() => setActionData({ expedition, action: "NOTIFICATION" })}
              className={`relative mb-2 flex h-12 w-12 [&_svg]:h-6 [&_svg]:w-6 ${hasUnreadNotif ? "bg-yellow-400 text-white hover:bg-yellow-500" : ""}`}
              type="button"
            >
              <Bell className="h-4 w-4" />
              <Badge variant="destructive" className="absolute top-0 right-0 h-4 w-4">
                {expedition.notification?.length}
              </Badge>
            </Button>
            <Button
              variant="outline"
              onClick={() => setActionData({ expedition, action: "CUSTOM_POLICY" })}
              type="button"
              className={`mb-2 h-12 w-12 [&_svg]:h-6 [&_svg]:w-6`}
            >
              <ClipboardList className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              onClick={() => openDraftModal()}
              type="button"
              className={`flex h-12 w-12 [&_svg]:h-6 [&_svg]:w-6 ${(isUpdateFinalized || expedition.processStatus === "VALIDATED") && "hidden"}`}
            >
              <Save className="h-4 w-4" />
            </Button>
          </PopoverContent>
        </Popover>
      </div>
      {actionData && actionData.action === "NOTES" && (
        <NotesModal
          expedition={actionData.expedition}
          open={true}
          onClose={() => setActionData(null)}
        />
      )}
      {actionData && actionData.action === "NOTIFICATION" && (
        <NotificationModal
          expeditionId={actionData.expedition.id}
          open={true}
          onClose={() => setActionData(null)}
        />
      )}
      {actionData && actionData.action === "DRAFT" && (
        <SaveDraftModal
          open={true}
          onClose={() => setActionData(null)}
          expeditionId={actionData.expedition.id}
          expeditionType={
            expedition.customQuotation
              ? ExpeditionTypeEnum.CUSTOM_QUOTATION
              : ExpeditionTypeEnum.QUOTATION
          }
          formStep={formStep}
        />
      )}
      {actionData && actionData.action === "CUSTOM_POLICY" && customId && (
        <CustomPolicyModal open={true} onClose={() => setActionData(null)} customId={customId} />
      )}
    </div>
  );
};

function ExpeditionHeaderApiEnumSelect({
  formStep,
  processStatus,
  expeditionType,
}: {
  formStep: number;
  processStatus: ExpeditionProcessStatusEnum;
  expeditionType: ExpeditionTypeEnum;
}) {
  const finalizeApiOptions = useFinalizeApiOptions();
  const { control } = useFormContext<QuotationUpdateBoFormData>();

  const apiOptions =
    expeditionType === ExpeditionTypeEnum.CUSTOM_QUOTATION
      ? finalizeApiOptions
      : finalizeApiOptions.slice(0, 2);

  return (
    <ControlledSelectV2
      control={control}
      name="apiEnum"
      options={apiOptions}
      searchable={false}
      clearable={false}
      isDisabled={formStep !== 1 || processStatus !== ExpeditionProcessStatusEnum.PENDING_PELICAN}
      wrapperClassName="min-w-[240px] shrink-0 h-full justify-center"
      contentClassName="h-13"
      renderOption={(option: Option) => {
        const meta = apiOptions.find((o) => o.value === option.value);
        if (!meta) return option.label;
        const Icon = meta.Icon;
        return (
          <span className="flex h-10 w-full items-center justify-between gap-2 pr-1">
            <span>{meta.headerSelect.optionLabel}</span>
            {Icon ? <Icon style={{ height: "32px", width: "auto" }} aria-hidden /> : null}
          </span>
        );
      }}
      renderValue={(selectedOption) => {
        const meta = apiOptions.find((o) => o.value === selectedOption?.value);
        if (!meta) return null;
        const Icon = meta.Icon;
        return (
          <span className="flex h-full items-center gap-2">
            {Icon ? <Icon style={{ height: "32px", width: "auto" }} aria-hidden /> : null}
            <span>{meta.headerSelect.triggerLabel}</span>
          </span>
        );
      }}
    />
  );
}
